import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { BookingFormModal, emptyBookingForm, type BookingForm, type GuestOption, type RoomOption as BRoomOption } from "../Bookings/BookingsPage";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus, faXmark, faPen, faTrash, faBed, faCheck, faCalendarDays, faList,
  faCheckCircle, faWrench, faTriangleExclamation, faChartPie,
  faUsers, faExpand, faWind, faEye, faTag, faHourglassHalf, faImage,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./RoomsPage.css";
import RoomRatesCalendar from "./RoomRatesCalendar";
import { OCCUPANCY_TIERS, OCC_LABEL, toOccForm, fromOccForm, type Occupancy } from "../../utils/occupancy";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const STATUS_OPTIONS = ["available", "maintenance", "out-of-service"];
const BED_TYPES = ["Single", "Double", "Queen", "King", "Twin", "Suite"];
const VIEW_OPTIONS = ["Sea", "Garden", "Pool", "City", "Mountain", "Courtyard", "None"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_W = 32;
const LABEL_W = 190;

type Room = {
  roomId: string;
  roomNumber: string | number;
  status: string;
  roomType: string;
  capacity: number;
  sizeSqm: number | null;
  bedType: string;
  floor: number | null;
  location: string;
  view: string;
  balconyTerrace: boolean;
  extraBed: boolean;
  smokingPolicy: string;
  description: string;
  specialFeatures: string;
  bedroomFeatures: string;
  bathroomAmenities: string;
  housekeepingServices: string;
};

type RoomForm = Omit<Room, "roomId">;

const emptyForm: RoomForm = {
  roomNumber: "",
  status: "available",
  roomType: "",
  capacity: 1,
  sizeSqm: null,
  bedType: "Double",
  floor: null,
  location: "",
  view: "None",
  balconyTerrace: false,
  extraBed: false,
  smokingPolicy: "Non-smoking",
  description: "",
  specialFeatures: "",
  bedroomFeatures: "",
  bathroomAmenities: "",
  housekeepingServices: "",
};

const FS: React.CSSProperties = {
  background: "#0E1E38",
  border: "1px solid rgba(201,168,76,0.2)",
  borderRadius: 10,
  padding: "10px 14px",
  color: "#F2EBD9",
  fontSize: 14,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
const LABEL: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.8px", color: "rgba(242,235,217,0.45)",
};

function normalizeStatus(s: string) {
  return (s ?? "").toLowerCase().replace(/\s+/g, "-");
}
function displayStatus(s: string) {
  return s.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function statusColor(s: string) {
  const n = normalizeStatus(s);
  if (n === "available")    return { color: "#34d399", bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.3)" };
  if (n === "occupied")     return { color: "#f59e0b", bg: "rgba(245,158,11,0.12)",  border: "rgba(245,158,11,0.3)" };
  if (n === "maintenance")  return { color: "#C9A84C", bg: "rgba(201,168,76,0.12)",  border: "rgba(14,34,64,0.10)" };
  return                           { color: "#f87171", bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.3)" };
}

function normalizeRoom(r: any): Room {
  return {
    roomId:            r.roomid ?? r.roomId ?? "",
    roomNumber:        r.roomnumber ?? r.roomNumber ?? "",
    status:            normalizeStatus(r.status ?? "available"),
    roomType:          r.roomtype ?? r.roomType ?? "",
    capacity:          Number(r.capacity ?? 1) || 1,
    sizeSqm:           r.size_sqm != null ? Number(r.size_sqm) : r.sizeSqm != null ? Number(r.sizeSqm) : null,
    bedType:           r.bed_type ?? r.bedType ?? "",
    floor:             r.floor ?? null,
    location:          r.location ?? "",
    view:              r.view ?? "None",
    balconyTerrace:    r.balcony_terrace ?? r.balconyTerrace ?? false,
    extraBed:          r.extra_bed ?? r.extraBed ?? false,
    smokingPolicy:     r.smoking_policy ?? r.smokingPolicy ?? "Non-smoking",
    description:       r.description ?? "",
    specialFeatures:   r.special_features ?? r.specialFeatures ?? "",
    bedroomFeatures:   r.bedroom_features ?? r.bedroomFeatures ?? "",
    bathroomAmenities: r.bathroom_amenities ?? r.bathroomAmenities ?? "",
    housekeepingServices: r.housekeeping_services ?? r.housekeepingServices ?? "",
  };
}

function formToBody(form: RoomForm, roomId?: string) {
  const body: any = {
    roomid: roomId ?? crypto.randomUUID(),
    roomnumber: form.roomNumber !== "" ? Number(form.roomNumber) : null,
    status: form.status,
    roomtype: form.roomType,
    capacity: form.capacity,
    size_sqm: form.sizeSqm,
    bed_type: form.bedType,
    floor: form.floor,
    location: form.location,
    view: form.view,
    balcony_terrace: form.balconyTerrace,
    extra_bed: form.extraBed,
    smoking_policy: form.smokingPolicy,
    description: form.description,
    special_features: form.specialFeatures,
    bedroom_features: form.bedroomFeatures,
    bathroom_amenities: form.bathroomAmenities,
    housekeeping_services: form.housekeepingServices,
  };
  return body;
}

// ── Room Form Modal ────────────────────────────────────────────
function RoomFormModal({
  mode, room, authHeaders, onClose, onSaved,
}: {
  mode: "add" | "edit";
  room?: Room;
  authHeaders: Record<string, string>;
  onClose: () => void;
  onSaved: (room: Room) => void;
}) {
  const [form, setForm] = useState<RoomForm>(
    room ? {
      roomNumber: room.roomNumber, status: room.status, roomType: room.roomType,
      capacity: room.capacity, sizeSqm: room.sizeSqm, bedType: room.bedType,
      floor: room.floor, location: room.location, view: room.view,
      balconyTerrace: room.balconyTerrace, extraBed: room.extraBed, smokingPolicy: room.smokingPolicy,
      description: room.description, specialFeatures: room.specialFeatures,
      bedroomFeatures: room.bedroomFeatures, bathroomAmenities: room.bathroomAmenities,
      housekeepingServices: room.housekeepingServices,
    } : { ...emptyForm }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = <K extends keyof RoomForm>(field: K, value: RoomForm[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const body = formToBody(form, mode === "edit" ? room?.roomId : undefined);
      const res = mode === "add"
        ? await axios.post(`${API_BASE}/api/rooms`, body, { headers: authHeaders })
        : await axios.put(`${API_BASE}/api/rooms/${room!.roomId}`, body, { headers: authHeaders });
      onSaved(normalizeRoom(res.data));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "An error occurred.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rp-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rp-modal">
        <div className="rp-modal-header">
          <h2>{mode === "add" ? "Add Room" : `Edit Room ${room?.roomNumber}`}</h2>
          <button className="rp-modal-close" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        {error && <div className="rp-error">{error}</div>}
        <form onSubmit={handleSubmit} className="rp-modal-form">
          <div className="rp-form-grid">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Room Number *</label>
              <input type="number" min={1} value={form.roomNumber} onChange={e => set("roomNumber", e.target.value)} required style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Room Type</label>
              <input type="text" placeholder="e.g. Deluxe, Suite" value={form.roomType} onChange={e => set("roomType", e.target.value)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)} style={FS}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Capacity</label>
              <input type="number" min={1} value={form.capacity} onChange={e => set("capacity", parseInt(e.target.value) || 1)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Bed Type</label>
              <select value={form.bedType} onChange={e => set("bedType", e.target.value)} style={FS}>
                {BED_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Floor</label>
              <input type="number" min={0} value={form.floor ?? ""} onChange={e => set("floor", e.target.value ? parseInt(e.target.value) : null)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Size (m²)</label>
              <input type="number" min={0} value={form.sizeSqm ?? ""} onChange={e => set("sizeSqm", e.target.value ? parseFloat(e.target.value) : null)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>View</label>
              <select value={form.view} onChange={e => set("view", e.target.value)} style={FS}>
                {VIEW_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Location / Wing</label>
              <input type="text" placeholder="e.g. East Wing" value={form.location} onChange={e => set("location", e.target.value)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Smoking Policy</label>
              <select value={form.smokingPolicy} onChange={e => set("smokingPolicy", e.target.value)} style={FS}>
                <option value="Non-smoking">Non-smoking</option>
                <option value="Smoking">Smoking</option>
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={form.balconyTerrace} onChange={e => set("balconyTerrace", e.target.checked)} style={{ width: 16, height: 16, accentColor: "#1a3a5c" }} />
                Has Balcony / Terrace
              </label>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={form.extraBed} onChange={e => set("extraBed", e.target.checked)} style={{ width: 16, height: 16, accentColor: "#1a3a5c" }} />
                Extra Bed Allowed
              </label>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 2" }}>
              <label style={LABEL}>Special Features</label>
              <input type="text" placeholder="e.g. Private pool, Jacuzzi" value={form.specialFeatures} onChange={e => set("specialFeatures", e.target.value)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 2" }}>
              <label style={LABEL}>Bedroom Features</label>
              <input type="text" placeholder="e.g. King bed, Smart TV" value={form.bedroomFeatures} onChange={e => set("bedroomFeatures", e.target.value)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 2" }}>
              <label style={LABEL}>Bathroom Amenities</label>
              <input type="text" placeholder="e.g. Bathtub, Rain shower" value={form.bathroomAmenities} onChange={e => set("bathroomAmenities", e.target.value)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 2" }}>
              <label style={LABEL}>Housekeeping Services</label>
              <input type="text" placeholder="e.g. Daily turndown" value={form.housekeepingServices} onChange={e => set("housekeepingServices", e.target.value)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 2" }}>
              <label style={LABEL}>Description</label>
              <textarea rows={3} value={form.description} onChange={e => set("description", e.target.value)} style={{ ...FS, resize: "vertical" }} />
            </div>
          </div>
          <div className="rp-modal-footer">
            <button type="button" className="rp-btn secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rp-btn primary" disabled={saving}>{saving ? "Saving…" : mode === "add" ? "Add Room" : "Save Changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Calendar Booking Mini-Modal ────────────────────────────────
function CalendarBookingModal({ room, checkIn, checkOut, authHeaders, guests, onClose, onSaved }: {
  room: Room; checkIn: string; checkOut: string;
  authHeaders: Record<string, string>;
  guests: { id?: number; name: string; phoneNumber: string; email?: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const nights = useMemo(() => {
    const d = (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000;
    return d > 0 ? d : 1;
  }, [checkIn, checkOut]);

  const [guestName, setGuestName] = useState("");
  const [phone, setPhone] = useState("");
  const [total, setTotal] = useState(0);
  const [autoCalc, setAutoCalc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dropOpen, setDropOpen] = useState(false);

  // Auto-calculate total from room rates
  useEffect(() => {
    if (!room.roomId || !checkIn || !checkOut) return;
    const token = localStorage.getItem("ai2_token") ?? "";
    const hotel = localStorage.getItem("ai2_hotel") ?? "";
    axios.get(`${API_BASE}/api/roomrates/calc`, {
      params: { roomId: room.roomId, checkin: checkIn, checkout: checkOut },
      headers: { Authorization: `Bearer ${token}`, "x-hotel": hotel },
    }).then(res => {
      if (res.data.total > 0) {
        setTotal(res.data.total);
        setAutoCalc(true);
      }
    }).catch(() => {});
  }, [room.roomId, checkIn, checkOut]);

  const filtered = useMemo(() =>
    guestName.trim()
      ? guests.filter(g => g.name.toLowerCase().includes(guestName.toLowerCase()))
      : guests
  , [guests, guestName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const rand = Math.floor(Math.random() * 900) + 100;
      const bookingId = `${room.roomId}-${checkIn.replace(/-/g, "")}-${rand}`;
      const cleanPhone = phone.replace(/\D/g, "");
      await axios.post(`${API_BASE}/api/dashboard/bookings`, {
        bookingid: bookingId, roomid: room.roomId,
        guestname: guestName,
        phonenumber: cleanPhone ? Number(cleanPhone) : null,
        checkin: checkIn, checkout: checkOut, nights, total,
        status: "confirmed",
      }, { headers: authHeaders });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "Error saving booking.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rp-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rp-modal" style={{ maxWidth: 460 }}>
        <div className="rp-modal-header">
          <div>
            <h2 style={{ margin: 0 }}>New Booking</h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "rgba(242,235,217,0.45)" }}>
              Room <strong style={{ color: "#C9A84C" }}>#{room.roomNumber}</strong>
              {room.roomType && ` · ${room.roomType}`}
            </p>
          </div>
          <button className="rp-modal-close" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>

        {error && <div className="rp-error">{error}</div>}

        <form onSubmit={handleSubmit} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Date chips */}
          <div style={{ display: "flex", gap: 10 }}>
            {[["Check In", checkIn], ["Check Out", checkOut], ["Nights", String(nights)]].map(([lbl, val]) => (
              <div key={lbl} style={{ flex: 1, background: "rgba(14,34,64,0.10)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 10, padding: "8px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "rgba(242,235,217,0.45)", marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#F2EBD9" }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Guest name */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative" }}>
            <label style={LABEL}>Guest Name *</label>
            <input type="text" value={guestName} required placeholder="Search or type name…"
              onChange={e => { setGuestName(e.target.value); setDropOpen(true); }}
              onFocus={() => setDropOpen(true)}
              onBlur={() => setTimeout(() => setDropOpen(false), 150)}
              style={FS} />
            {dropOpen && filtered.length > 0 && (
              <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 300,
                background: "#0E1E38", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 10,
                maxHeight: 180, overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                {filtered.slice(0, 20).map(g => (
                  <div key={g.id ?? g.phoneNumber}
                    onMouseDown={() => { setGuestName(g.name); setPhone(g.phoneNumber); setDropOpen(false); }}
                    style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                    onMouseEnter={e => (e.currentTarget.style.background = "rgba(201,168,76,0.12)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: "#F2EBD9", fontWeight: 600 }}>{g.name}</span>
                      <span style={{ fontSize: 12, color: "#C9A84C", fontFamily: "monospace" }}>{g.phoneNumber}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Phone</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+20 10…" style={FS} />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...LABEL, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Total (EGP)</span>
                {autoCalc && <span style={{ fontSize: 9, color: "#34d399", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>Auto-calculated</span>}
              </label>
              <input type="number" min={0} value={total} onChange={e => { setTotal(parseFloat(e.target.value) || 0); setAutoCalc(false); }} style={FS} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
            <button type="button" className="rp-btn secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rp-btn primary" disabled={saving}>
              {saving ? "Saving…" : "Add Booking"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function fmtShort(iso: string): string {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function TooltipRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 3 }}>
      <span style={{ fontSize: 12, color: "rgba(242,235,217,0.45)" }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: color || "#94a3b8" }}>{value}</span>
    </div>
  );
}

function PopupRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ fontSize: 12, color: "rgba(242,235,217,0.45)" }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: highlight ? "#C9A84C" : "rgba(242,235,217,0.6)" }}>{value}</span>
    </div>
  );
}

// ── Calendar Tab ───────────────────────────────────────────────
function RoomCalendar({ rooms, authHeaders, seasons }: {
  rooms: Room[];
  authHeaders: Record<string, string>;
  seasons: { id: number; season: string; start_date: string; end_date: string }[];
}) {
  const [avail, setAvail] = useState<any[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [guests, setGuests] = useState<{ id?: number; name: string; phoneNumber: string; email?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const calRef = useRef<HTMLDivElement>(null);

  // Tooltip
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: React.ReactNode } | null>(null);

  // Drag selection
  const isDragging = useRef(false);
  const [selRoom, setSelRoom] = useState<Room | null>(null);
  const [selStart, setSelStart] = useState("");
  const [selEnd, setSelEnd] = useState("");
  const [bookingModal, setBookingModal] = useState<{ room: Room; checkIn: string; checkOut: string } | null>(null);

  const fetchAvailability = useCallback(async (y: number) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/rooms/availability/range`, {
        params: { from: `${y}-01-01`, to: `${y}-12-31` },
        headers: authHeaders,
      });
      setAvail(Array.isArray(res.data) ? res.data : []);
    } catch { setAvail([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAvailability(year); }, [year]);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/dashboard/bookings`, { headers: authHeaders });
      // calendarEntries: one entry per booking_rooms row, has correct roomid + leaderName/leaderPhone/total
      setBookings(res.data?.calendarEntries ?? res.data?.bookings ?? []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/api/guests`, { headers: authHeaders })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data?.guests ?? [];
        setGuests(data.map((g: any) => ({ id: g.id, name: g.name, phoneNumber: String(g.phoneNumber ?? "") })));
      }).catch(() => {});
    fetchBookings();
  }, []);

  // Scroll to today
  useEffect(() => {
    if (!loading && calRef.current) {
      const today = new Date();
      if (today.getFullYear() === year) {
        const dayOfYear = Math.floor((today.getTime() - new Date(year, 0, 1).getTime()) / 86400000);
        calRef.current.scrollLeft = Math.max(0, dayOfYear * DAY_W - 300);
      }
    }
  }, [loading, year]);

  // Global mouseup — just end drag, keep selection visible
  useEffect(() => {
    const up = () => { isDragging.current = false; };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const [syncing, setSyncing] = useState(false);

  type BarData = {
    leaderName: string; leaderPhone: string; guestname: string;
    checkIn: string; checkOut: string; nights: number;
    bookingId: string; total: number; status: string;
  };
  const [barTooltip, setBarTooltip] = useState<(BarData & { x: number; y: number }) | null>(null);
  const [barPopup, setBarPopup] = useState<(BarData & { x: number; y: number }) | null>(null);

  const handleBarEnter = (e: React.MouseEvent, bar: BarData) => {
    setBarTooltip({ x: e.clientX, y: e.clientY, ...bar });
  };
  const handleBarLeave = () => setBarTooltip(null);
  const handleBarClick = (e: React.MouseEvent, bar: BarData) => {
    e.stopPropagation();
    setBarTooltip(null);
    setBarPopup(prev => prev && prev.bookingId === bar.bookingId ? null : { x: e.clientX, y: e.clientY, ...bar });
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API_BASE}/api/rooms/seed-availability`, { year }, { headers: authHeaders });
      await Promise.all([fetchAvailability(year), fetchBookings()]);
    } catch (err: any) {
      alert(err?.response?.data?.error ?? "Seed failed");
    } finally { setSeeding(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API_BASE}/api/rooms/sync-availability`, {}, { headers: authHeaders });
      await Promise.all([fetchAvailability(year), fetchBookings()]);
    } catch (err: any) {
      alert(err?.response?.data?.error ?? "Sync failed");
    } finally { setSyncing(false); }
  };

  const localISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

  // bookedSet: merge rooms_availability data AND calendarEntries (per-room)
  const bookedSet = useMemo(() => {
    const set = new Set<string>();
    for (const row of avail) {
      if (row.availability === false || row.availability === "false")
        set.add(`${row.roomid}|${String(row.availability_date).slice(0, 10)}`);
    }
    for (const b of bookings) {
      const roomId = b.roomid ?? "";
      const ci = (b.checkin ?? "").slice(0, 10);
      const co = (b.checkout ?? "").slice(0, 10);
      if (!roomId || !ci || !co) continue;
      if (["cancelled", "deleted"].includes((b.status ?? "").toLowerCase())) continue;
      const cur = new Date(ci + "T12:00:00");
      const end = new Date(co + "T12:00:00");
      while (cur < end) {
        set.add(`${roomId}|${localISO(cur)}`);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return set;
  }, [avail, bookings]);

  // onHoldSet: dates that are on-hold (unexpired)
  const onHoldSet = useMemo(() => {
    const set = new Set<string>();
    const nowTs = Date.now();
    for (const row of avail) {
      if (row.availability === "on-hold") {
        if (row.held_until && new Date(row.held_until).getTime() < nowTs) continue;
        set.add(`${row.roomid}|${String(row.availability_date).slice(0, 10)}`);
      }
    }
    return set;
  }, [avail]);

  const days = useMemo(() => {
    const result: { date: Date; iso: string; monthStart: boolean }[] = [];
    const d = new Date(year, 0, 1);
    while (d.getFullYear() === year) {
      result.push({ date: new Date(d), iso: localISO(d), monthStart: d.getDate() === 1 });
      d.setDate(d.getDate() + 1);
    }
    return result;
  }, [year]);

  // Booking bars per room: one bar per calendarEntry, for the visual gantt overlay
  const STATUS_BAR_COLOR: Record<string, string> = {
    confirmed: "#3b6ea5",
    "checked-in": "#10b981",
    "checked-out": "#64748b",
    pending: "#f59e0b",
    "pending payment": "#f59e0b",
    "on-hold": "#f97316",
  };
  const bookingBars = useMemo(() => {
    const yearStart = new Date(year, 0, 1, 12, 0, 0).getTime();
    const totalDays = days.length;
    const barsByRoom: Record<string, Array<{
      left: number; width: number;
      leaderName: string; leaderPhone: string; guestname: string;
      checkIn: string; checkOut: string; nights: number;
      bookingId: string; total: number; status: string;
    }>> = {};
    const seen = new Set<string>();
    for (const b of bookings) {
      const roomId = b.roomid ?? "";
      const ci = (b.checkin ?? "").slice(0, 10);
      const co = (b.checkout ?? "").slice(0, 10);
      if (!roomId || !ci || !co) continue;
      if (["cancelled", "deleted"].includes((b.status ?? "").toLowerCase())) continue;
      const dedupeKey = `${b.bookingid ?? ""}::${roomId}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      const ciTs = new Date(ci + "T12:00:00").getTime();
      const coTs = new Date(co + "T12:00:00").getTime();
      const startDay = Math.round((ciTs - yearStart) / 86400000);
      const endDay   = Math.round((coTs - yearStart) / 86400000);
      if (endDay <= 0 || startDay >= totalDays) continue;
      const clampedStart = Math.max(0, startDay);
      const clampedEnd   = Math.min(totalDays, endDay);
      const left  = clampedStart * DAY_W + 2;
      const width = (clampedEnd - clampedStart) * DAY_W - 4;
      if (width <= 0) continue;
      if (!barsByRoom[roomId]) barsByRoom[roomId] = [];
      barsByRoom[roomId].push({
        left, width,
        leaderName: b.leaderName || b.guestname || "—",
        leaderPhone: b.leaderPhone || "",
        guestname: b.guestname || "—",
        checkIn: ci,
        checkOut: co,
        nights: b.nights || Math.round((coTs - ciTs) / 86400000),
        bookingId: b.bookingid || "",
        total: Number(b.total) || 0,
        status: b.status || "confirmed",
      });
    }
    return barsByRoom;
  }, [bookings, year, days]);

  const todayIso = useMemo(() => localISO(new Date()), []);
  const sortedRooms = useMemo(() => [...rooms].sort((a, b) => Number(a.roomNumber) - Number(b.roomNumber)), [rooms]);

  const selMin = selStart && selEnd ? (selStart < selEnd ? selStart : selEnd) : "";
  const selMax = selStart && selEnd ? (selStart < selEnd ? selEnd : selStart) : "";

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "rgba(242,235,217,0.45)", gap: 12, fontSize: 14 }}>
      <div className="rp-spinner" /> Loading calendar…
    </div>
  );

  const hasData = avail.length > 0;

  return (
    <div className="rp-calendar-wrap" onClick={() => setBarPopup(null)}>
      {/* Controls bar */}
      <div className="rp-cal-controls">
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#0E1E38", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 10, padding: "4px 6px" }}>
          <button className="rp-cal-nav" onClick={() => setYear(y => y - 1)}>‹</button>
          <span className="rp-cal-year">{year}</span>
          <button className="rp-cal-nav" onClick={() => setYear(y => y + 1)}>›</button>
        </div>
        <button className="rp-btn secondary" style={{ fontSize: 12, padding: "7px 14px" }}
          onClick={() => { setYear(new Date().getFullYear()); setTimeout(() => { if (calRef.current) { const doy = Math.floor((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000); calRef.current.scrollLeft = Math.max(0, doy * DAY_W - 300); }}, 100); }}>
          Today
        </button>
        <button className="rp-btn primary" onClick={handleSeed} disabled={seeding} style={{ fontSize: 12, padding: "7px 14px" }}>
          {seeding ? "Seeding…" : hasData ? "Re-seed" : "Seed 365 Days"}
        </button>
        <div className="rp-cal-legend">
          <span className="rp-legend-item"><span className="rp-legend-dot booked" />Booked</span>
          <span className="rp-legend-item"><span className="rp-legend-dot on-hold" />On Hold</span>
          <span className="rp-legend-item"><span className="rp-legend-dot available" />Available</span>
          <span className="rp-legend-item"><span className="rp-legend-dot selected" />Selected</span>
          <span className="rp-legend-item"><span className="rp-legend-dot today-dot" />Today</span>
        </div>
      </div>

      {!hasData && (
        <div className="rp-cal-empty">
          No availability data. Click <strong>Seed 365 Days</strong> to generate the calendar.
        </div>
      )}

      <div className="rp-cal-scroll" ref={calRef} style={{ userSelect: "none" }}>
        <div style={{ display: "inline-flex", flexDirection: "column", minWidth: LABEL_W + days.length * DAY_W }}>

          {/* Sticky header rows */}
          <div style={{ position: "sticky", top: 0, zIndex: 10, background: "#0a1120" }}>
            {/* Month headers */}
            <div style={{ display: "flex", marginLeft: LABEL_W }}>
              {MONTHS.map((m, mi) => {
                const dim = new Date(year, mi + 1, 0).getDate();
                return (
                  <div key={m} className="rp-month-header" style={{ width: dim * DAY_W }}>
                    {m}
                  </div>
                );
              })}
            </div>
            {/* Day numbers */}
            <div style={{ display: "flex", marginLeft: LABEL_W }}>
              {days.map(d => {
                const isWknd = d.date.getDay() === 0 || d.date.getDay() === 6;
                const isTod = d.iso === todayIso;
                return (
                  <div key={d.iso} className={`rp-day-header ${isTod ? "today" : ""} ${isWknd ? "weekend" : ""}`}
                    style={{ width: DAY_W, borderLeft: d.monthStart ? "1px solid rgba(201,168,76,0.2)" : undefined }}>
                    {d.date.getDate()}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Room rows */}
          {sortedRooms.map(room => {
            // Occupied = today's date is booked in rooms_availability (via bookedSet)
            const isOccupiedToday = bookedSet.has(`${room.roomId}|${todayIso}`);
            const effectiveStatus = isOccupiedToday ? "occupied" : room.status;
            const sc = statusColor(effectiveStatus);
            return (
              <div key={room.roomId} className="rp-cal-row">
                {/* Room label */}
                <div className="rp-cal-label" style={{ width: LABEL_W }}>
                  {room.roomType && <span className="rp-cal-room-type">{room.roomType}</span>}
                  <div className="rp-cal-room-name-row">
                    <span className="rp-cal-room-num">Room {room.roomNumber}</span>
                    <span className="rp-cal-room-status" style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}>
                      {displayStatus(effectiveStatus)}
                    </span>
                  </div>
                  <span className="rp-cal-room-id">{room.roomId}</span>
                </div>

                {/* Day cells + bars overlay */}
                <div style={{ position: "relative", flex: 1, height: "100%", minWidth: 0 }}>
                <div style={{ display: "flex", height: "100%" }}>
                  {days.map((d, di) => {
                    const booked = bookedSet.has(`${room.roomId}|${d.iso}`);
                    const onHold = !booked && onHoldSet.has(`${room.roomId}|${d.iso}`);
                    const isBlocked = booked || onHold;
                    const isTod = d.iso === todayIso;
                    const isSel = selRoom?.roomId === room.roomId && selMin && selMax && d.iso >= selMin && d.iso <= selMax;
                    const prevBooked = di > 0 && bookedSet.has(`${room.roomId}|${days[di - 1].iso}`);
                    const nextBooked = di < days.length - 1 && bookedSet.has(`${room.roomId}|${days[di + 1].iso}`);
                    const prevOnHold = di > 0 && onHoldSet.has(`${room.roomId}|${days[di - 1].iso}`);
                    const nextOnHold = di < days.length - 1 && onHoldSet.has(`${room.roomId}|${days[di + 1].iso}`);
                    const isFirst = booked && !prevBooked;
                    const isLast  = booked && !nextBooked;
                    const isFirstHold = onHold && !prevOnHold;
                    const isLastHold  = onHold && !nextOnHold;

                    return (
                      <div key={d.iso}
                        className={`rp-cal-cell ${isTod ? "today" : ""} ${d.monthStart ? "month-start" : ""}`}
                        style={{ width: DAY_W, cursor: isBlocked ? "default" : "crosshair" }}
                        onMouseDown={() => { if (isBlocked) return; isDragging.current = true; setSelRoom(room); setSelStart(d.iso); setSelEnd(d.iso); }}
                        onMouseEnter={e => {
                          if (isDragging.current && selRoom?.roomId === room.roomId) {
                            if (isBlocked) return;
                            setSelEnd(d.iso); return;
                          }
                          if (onHold) {
                            setTooltip({
                              x: e.clientX, y: e.clientY,
                              content: (
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", flexShrink: 0 }} />
                                    <span style={{ fontWeight: 700, color: "#F2EBD9", fontSize: 13 }}>On Hold</span>
                                  </div>
                                  <div style={{ fontSize: 11, color: "#f97316" }}>WhatsApp booking — 2-day hold</div>
                                  <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{d.iso}</div>
                                </div>
                              ),
                            });
                          }
                        }}
                        onMouseMove={e => { if (tooltip) setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null); }}
                        onMouseLeave={() => setTooltip(null)}
                      >
                        {/* Booking bars are drawn once as the absolute overlay below
                            (rp-booked-bar / rp-onhold-bar removed to avoid double-rendering). */}
                        {/* Selected overlay */}
                        {isSel && !isBlocked && <div className="rp-sel-bar" style={{ borderRadius: d.iso === selMin && d.iso === selMax ? 6 : d.iso === selMin ? "6px 0 0 6px" : d.iso === selMax ? "0 6px 6px 0" : 0 }} />}
                        {/* Today line */}
                        {isTod && !booked && <div className="rp-today-line" />}
                      </div>
                    );
                  })}
                </div>
                {/* Booking bars overlay — shows guest name spans */}
                <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, right: 0, pointerEvents: "none" }}>
                  {(bookingBars[room.roomId] || []).map((bar, bi) => (
                    <div key={bi}
                      style={{
                        position: "absolute",
                        left: bar.left,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: bar.width,
                        height: 26,
                        borderRadius: 6,
                        background: STATUS_BAR_COLOR[bar.status] || "#1a3a5c",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: 8,
                        paddingRight: 6,
                        overflow: "hidden",
                        zIndex: 4,
                        pointerEvents: "all",
                        cursor: "pointer",
                        userSelect: "none",
                        transition: "opacity 0.15s, box-shadow 0.15s",
                        boxSizing: "border-box",
                      }}
                      onMouseEnter={(e) => handleBarEnter(e, bar)}
                      onMouseLeave={handleBarLeave}
                      onClick={(e) => handleBarClick(e, bar)}
                    >
                      {bar.width > 64 && (
                        <span style={{
                          fontSize: 11, fontWeight: 600,
                          color: "rgba(255,255,255,0.92)",
                          whiteSpace: "nowrap", overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: bar.width - 16, display: "block",
                        }}>
                          {bar.leaderName}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                </div>{/* close relative wrapper */}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selection bar */}
      {selRoom && selMin && selMax && !bookingModal && (
        <div className="rp-sel-bar-strip">
          <div className="rp-sel-info">
            <span className="rp-sel-room">Room #{selRoom.roomNumber}</span>
            <span className="rp-sel-dates">{selMin} → {selMax}</span>
            <span className="rp-sel-nights">
              {Math.round((new Date(selMax).getTime() - new Date(selMin).getTime()) / 86400000) + 1} night{Math.round((new Date(selMax).getTime() - new Date(selMin).getTime()) / 86400000) + 1 !== 1 ? "s" : ""}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="rp-btn secondary" style={{ fontSize: 12, padding: "6px 14px" }}
              onClick={() => { setSelRoom(null); setSelStart(""); setSelEnd(""); }}>
              Clear
            </button>
            <button className="rp-btn primary" style={{ fontSize: 12, padding: "6px 14px" }}
              onClick={() => {
                const co = new Date(selMax + "T12:00:00");
                co.setDate(co.getDate() + 1);
                setBookingModal({ room: selRoom, checkIn: selMin, checkOut: localISO(co) });
              }}>
              Book Selected Days
            </button>
          </div>
        </div>
      )}

      {/* Full booking modal from calendar selection */}
      {bookingModal && (
        <BookingFormModal
          mode="add"
          initial={{
            ...emptyBookingForm,
            roomId: bookingModal.room.roomId,
            roomIds: [bookingModal.room.roomId],
            checkIn: bookingModal.checkIn,
            checkOut: bookingModal.checkOut,
            nights: Math.round((new Date(bookingModal.checkOut).getTime() - new Date(bookingModal.checkIn).getTime()) / 86400000),
          }}
          guests={guests}
          rooms={rooms.map(r => ({ roomId: r.roomId, roomNumber: String(r.roomNumber), roomType: r.roomType, status: r.status }))}
          seasons={seasons}
          allBookings={bookings}
          authHeaders={authHeaders}
          onClose={() => { setBookingModal(null); setSelRoom(null); setSelStart(""); setSelEnd(""); }}
          onSaved={() => {
            setBookingModal(null); setSelRoom(null); setSelStart(""); setSelEnd("");
            fetchAvailability(year);
            fetchBookings();
          }}
        />
      )}

      {/* On-hold cell tooltip */}
      {tooltip && (
        <div className="rp-tooltip" style={{ left: tooltip.x + 14, top: tooltip.y - 10 }}>
          {tooltip.content}
        </div>
      )}

      {/* Bar hover tooltip */}
      {barTooltip && !barPopup && (
        <div style={{
          position: "fixed",
          left: barTooltip.x + 14,
          top: barTooltip.y - 10,
          zIndex: 9999,
          background: "#0E1E38",
          border: "1px solid rgba(201,168,76,0.2)",
          borderRadius: 10,
          padding: "10px 14px",
          minWidth: 200,
          maxWidth: 260,
          pointerEvents: "none",
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          color: "#F2EBD9",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#F2EBD9", marginBottom: 1 }}>{barTooltip.leaderName}</div>
          {barTooltip.leaderPhone && (
            <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginBottom: 2 }}>{barTooltip.leaderPhone}</div>
          )}
          {barTooltip.leaderName !== barTooltip.guestname && barTooltip.guestname !== "—" && (
            <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", marginBottom: 6 }}>Booked by: {barTooltip.guestname}</div>
          )}
          {!barTooltip.leaderPhone && !(barTooltip.leaderName !== barTooltip.guestname && barTooltip.guestname !== "—") && <div style={{ marginBottom: 6 }} />}
          <TooltipRow label="Check-in" value={fmtShort(barTooltip.checkIn)} />
          <TooltipRow label="Check-out" value={fmtShort(barTooltip.checkOut)} />
          <TooltipRow label="Nights" value={String(barTooltip.nights)} />
          <TooltipRow label="Status" value={barTooltip.status} color={STATUS_BAR_COLOR[barTooltip.status] || "#94a3b8"} />
          <TooltipRow label="Total" value={`EGP ${barTooltip.total.toLocaleString()}`} color="#C9A84C" />
        </div>
      )}

      {/* Bar click popup */}
      {barPopup && (
        <div style={{
          position: "fixed",
          left: barPopup.x + 14,
          top: barPopup.y - 10,
          zIndex: 10000,
          background: "#0E1E38",
          border: "1px solid rgba(201,168,76,0.2)",
          borderRadius: 12,
          padding: "14px 16px",
          minWidth: 230,
          maxWidth: 270,
          boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
          color: "#F2EBD9",
        }} onClick={e => e.stopPropagation()}>
          <button style={{
            position: "absolute", top: 8, right: 10,
            background: "none", border: "none",
            color: "rgba(242,235,217,0.35)",
            fontSize: 16, cursor: "pointer", lineHeight: 1,
          }} onClick={() => setBarPopup(null)}>×</button>
          <div style={{ paddingRight: 20, marginBottom: 2 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#F2EBD9" }}>{barPopup.leaderName}</div>
            {barPopup.leaderPhone && (
              <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 1 }}>{barPopup.leaderPhone}</div>
            )}
            {barPopup.leaderName !== barPopup.guestname && barPopup.guestname !== "—" && (
              <div style={{ fontSize: 11, color: "rgba(242,235,217,0.5)", marginTop: 3 }}>Booked by: {barPopup.guestname}</div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, marginTop: 6, flexWrap: "wrap" }}>
            <div style={{
              display: "inline-block",
              background: `${STATUS_BAR_COLOR[barPopup.status] || "#1a3a5c"}22`,
              border: `1px solid ${STATUS_BAR_COLOR[barPopup.status] || "#1a3a5c"}66`,
              color: STATUS_BAR_COLOR[barPopup.status] || "#94a3b8",
              borderRadius: 6, padding: "2px 10px",
              fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>
              {barPopup.status}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <PopupRow label="Booking ID" value={barPopup.bookingId} />
            <PopupRow label="Check-in" value={fmtShort(barPopup.checkIn)} />
            <PopupRow label="Check-out" value={fmtShort(barPopup.checkOut)} />
            <PopupRow label="Nights" value={String(barPopup.nights)} />
            <PopupRow label="Total" value={`EGP ${barPopup.total.toLocaleString()}`} highlight />
          </div>
        </div>
      )}
    </div>
  );
}


// ── Room Type Avg Prices Tab ──────────────────────────────────
const API_TR = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

function RoomTypeRatesTab({
  rooms,
  authHeaders,
  onRoomsChanged,
}: {
  rooms: Room[];
  authHeaders: Record<string, string>;
  onRoomsChanged?: () => void;
}) {
  // Derive unique types + counts from rooms list
  const typeGroups = useMemo(() => {
    const map: Record<string, number> = {};
    rooms.forEach(r => { if (r.roomType) map[r.roomType] = (map[r.roomType] || 0) + 1; });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rooms]);

  const [prices, setPrices] = useState<Record<string, Record<Occupancy, string>>>({});
  const [saved,  setSaved]  = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [toast,  setToast]  = useState("");
  const [loaded, setLoaded] = useState(false);

  // Load existing per-occupancy avg prices (legacy number values map to the Double tier)
  useEffect(() => {
    setLoaded(false);
    axios.get(`${API_TR}/api/roomrates/type-avg`, { headers: authHeaders })
      .then(res => {
        const stored: Record<string, any> = res.data || {};
        setPrices(prev => {
          const next = { ...prev };
          typeGroups.forEach(([t]) => { if (!next[t]) next[t] = toOccForm(stored[t]); });
          Object.entries(stored).forEach(([t, v]) => { if (!next[t]) next[t] = toOccForm(v); });
          return next;
        });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders, typeGroups.length]);

  const handleChange = (type: string, occ: Occupancy, val: string) => {
    setPrices(p => ({ ...p, [type]: { ...(p[type] || { single: "", double: "", triple: "", quad: "" }), [occ]: val } }));
    setSaved(s => ({ ...s, [type]: false }));
  };

  const saveAll = async () => {
    setSaving(true);
    setToast("");
    try {
      const typeRates: Record<string, any> = {};
      typeGroups.forEach(([t]) => {
        const obj = fromOccForm(prices[t] || { single: "", double: "", triple: "", quad: "" });
        if (obj) typeRates[t] = obj;
      });
      if (Object.keys(typeRates).length === 0) {
        setToast("Enter at least one price before saving");
        setTimeout(() => setToast(""), 3000);
        return;
      }
      await axios.put(`${API_TR}/api/roomrates/type-avg`, { typeRates }, { headers: authHeaders });
      const newSaved: Record<string, boolean> = {};
      Object.keys(typeRates).forEach(t => { newSaved[t] = true; });
      setSaved(newSaved);
      setToast(`Saved — base rates updated for ${Object.keys(typeRates).length} type${Object.keys(typeRates).length !== 1 ? "s" : ""}`);
      setTimeout(() => setToast(""), 4000);
      onRoomsChanged?.(); // refresh rooms list in case new rooms were created
    } catch (e: any) {
      setToast(e?.response?.data?.error || "Failed to save");
      setTimeout(() => setToast(""), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div style={{ padding: 40, color: "rgba(242,235,217,0.4)", textAlign: "center" }}>Loading…</div>;

  const allTypes = [
    ...typeGroups.map(([t, c]) => ({ type: t, count: c })),
    // types returned from API that don't have rooms in DB yet
    ...Object.keys(prices)
      .filter(t => !typeGroups.find(([g]) => g === t))
      .map(t => ({ type: t, count: 0 })),
  ];
  const blankOcc: Record<Occupancy, string> = { single: "", double: "", triple: "", quad: "" };

  return (
    <div className="rp-type-rates">
      <div className="rp-type-rates-header">
        <div>
          <h3 className="rp-type-rates-title">Average Price by Room Type</h3>
          <p className="rp-type-rates-sub">Sets the base rate for every room of that type in the rates calendar. Also auto-creates any missing rooms from your hotel definition.</p>
        </div>
        <button className="rp-btn primary" onClick={saveAll} disabled={saving}>
          {saving ? "Saving…" : "Save All"}
        </button>
      </div>

      {toast && (
        <div className={`rp-type-rates-toast ${toast.includes("Failed") || toast.includes("Enter") ? "error" : ""}`}>{toast}</div>
      )}

      <div className="rp-type-rates-grid">
        {allTypes.map(({ type, count }) => (
          <div key={type} className={`rp-type-card ${saved[type] ? "saved" : ""}`}>
            <div className="rp-type-card-top">
              <span className="rp-type-card-name">{type}</span>
              <span className="rp-type-card-count">{count > 0 ? `${count} room${count !== 1 ? "s" : ""}` : "from definition"}</span>
            </div>
            <div className="rp-type-card-body">
              <label className="rp-type-card-label">Avg Price / Night — by occupancy</label>
              {OCCUPANCY_TIERS.map(occ => (
                <div key={occ} className="rp-type-card-input-wrap" style={{ marginBottom: 6 }}>
                  <span className="rp-type-card-currency" style={{ minWidth: 52 }}>{OCC_LABEL[occ]}</span>
                  <input
                    type="number"
                    min={0}
                    className="rp-type-card-input"
                    placeholder={occ === "double" ? "0" : "—"}
                    value={(prices[type] ?? blankOcc)[occ] ?? ""}
                    onChange={e => handleChange(type, occ, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="rp-type-card-footer">
              {count > 0
                ? `Updates base rate for all ${count} ${type} room${count !== 1 ? "s" : ""}`
                : `Will create rooms from hotel definition on save`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Room Photos tab — bulk-upload photos per room type (bot sends them on request) ──
function RoomTypePhotosTab({ rooms, authHeaders }: {
  rooms: Room[];
  authHeaders: Record<string, string>;
}) {
  const types = useMemo(
    () => [...new Set(rooms.map(r => r.roomType).filter(Boolean))].sort(),
    [rooms]
  );
  const [photos, setPhotos] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    axios.get(`${API_TR}/api/rooms/type-photos`, { headers: authHeaders })
      .then(res => { setPhotos(res.data || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, [authHeaders]);

  const upload = async (type: string, files: FileList | null) => {
    if (!files || !files.length) return;
    setBusy(type); setToast("");
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append("files", f));
      // Do NOT set Content-Type manually — axios/browser adds the multipart boundary itself.
      const res = await axios.post(`${API_TR}/api/rooms/type-photos/${encodeURIComponent(type)}`, fd, {
        headers: authHeaders,
      });
      setPhotos(p => ({ ...p, [type]: res.data.images || [] }));
      setToast(`Added ${res.data.added} photo${res.data.added !== 1 ? "s" : ""} to ${type}`);
    } catch (e: any) {
      setToast(e?.response?.data?.error || "Upload failed");
    } finally { setBusy(""); setTimeout(() => setToast(""), 3500); }
  };

  const remove = async (type: string, url: string) => {
    try {
      const res = await axios.delete(`${API_TR}/api/rooms/type-photos/${encodeURIComponent(type)}`, {
        headers: authHeaders, data: { url },
      });
      setPhotos(p => ({ ...p, [type]: res.data.images || [] }));
    } catch { /* ignore */ }
  };

  if (loading) return <div style={{ padding: 40, color: "rgba(242,235,217,0.4)", textAlign: "center" }}>Loading…</div>;

  return (
    <div className="rp-type-rates">
      <div className="rp-type-rates-header">
        <div>
          <h3 className="rp-type-rates-title">Room Photos</h3>
          <p className="rp-type-rates-sub">Upload photos per room type. The WhatsApp bot sends these when a guest asks to see the room (max 4 sent).</p>
        </div>
      </div>
      {toast && <div className={`rp-type-rates-toast ${/fail/i.test(toast) ? "error" : ""}`}>{toast}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {types.map(type => (
          <div key={type} className="rp-type-card" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span className="rp-type-card-name">{type} <span style={{ fontSize: 12, opacity: 0.5 }}>({(photos[type] || []).length})</span></span>
              <label className="rp-btn primary" style={{ cursor: busy === type ? "wait" : "pointer", opacity: busy === type ? 0.6 : 1 }}>
                {busy === type ? "Uploading…" : <><FontAwesomeIcon icon={faImage} /> Add Photos</>}
                <input type="file" accept="image/*" multiple style={{ display: "none" }}
                  disabled={busy === type}
                  onChange={e => { upload(type, e.target.files); e.target.value = ""; }} />
              </label>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {(photos[type] || []).length === 0 && (
                <span style={{ fontSize: 12, color: "rgba(242,235,217,0.4)" }}>No photos yet — guests won't be sent any for this room.</span>
              )}
              {(photos[type] || []).map(url => (
                <div key={url} style={{ position: "relative", width: 120, height: 90, borderRadius: 8, overflow: "hidden", background: "rgba(255,255,255,0.04)" }}>
                  <img src={url} alt={type} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                  <button onClick={() => remove(type, url)} title="Remove"
                    style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: 6, color: "#fff", width: 24, height: 24, cursor: "pointer" }}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function RoomsPage() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchParams] = useSearchParams();
  const getTabFromParams = () => {
    const t = searchParams.get("tab");
    if (t === "calendar") return "calendar" as const;
    if (t === "inhouse") return "inhouse" as const;
    if (t === "rates") return "rates" as const;
    if (t === "onhold") return "onhold" as const;
    return "list" as const;
  };
  const [tab, setTab] = useState<"list" | "calendar" | "inhouse" | "rates" | "type-rates" | "photos" | "onhold">(getTabFromParams);

  useEffect(() => {
    setTab(getTabFromParams());
  }, [searchParams]);
  const [inHouseRooms, setInHouseRooms] = useState<any[]>([]);
  const [inHouseLoading, setInHouseLoading] = useState(false);

  // Per-room orders drill-down (itemized list behind the "Orders" folio figure)
  const [ordersModal, setOrdersModal] = useState<{ roomNumber: string; orders: any[]; loading: boolean } | null>(null);
  const openRoomOrders = async (roomNumber: string | number) => {
    const room = String(roomNumber);
    setOrdersModal({ roomNumber: room, orders: [], loading: true });
    try {
      const { data } = await axios.get(`${API_BASE}/api/roomservice/orders?room=${encodeURIComponent(room)}`, { headers: authHeaders });
      setOrdersModal({ roomNumber: room, orders: data || [], loading: false });
    } catch {
      setOrdersModal({ roomNumber: room, orders: [], loading: false });
    }
  };

  // On-hold tab
  const [onHoldList, setOnHoldList] = useState<any[]>([]);
  const [onHoldLoading, setOnHoldLoading] = useState(false);
  const [onHoldActing, setOnHoldActing] = useState<string>(""); // bookingid being acted on

  // Booking form for vacant room
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [bookingFormInitial, setBookingFormInitial] = useState<BookingForm & { bookingId?: string }>({ ...emptyBookingForm });
  const [guestsList, setGuestsList] = useState<GuestOption[]>([]);
  const [roomsList, setRoomsList] = useState<BRoomOption[]>([]);
  const [seasonsList, setSeasonsList] = useState<{ id: number; season: string; start_date: string; end_date: string }[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [editRoom, setEditRoom] = useState<Room | undefined>(undefined);
  const [toast, setToast] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<Room | null>(null);
  const [roomStatusCounts, setRoomStatusCounts] = useState<{ ready: number; occupied: number; cleaning: number; outOfService: number; total: number } | null>(null);
  const [todayOccupiedIds, setTodayOccupiedIds] = useState<Set<string>>(new Set());

  const token = localStorage.getItem("ai2_token") ?? "";
  const hotel = localStorage.getItem("ai2_hotel") ?? "";
  const authHeaders = { Authorization: `Bearer ${token}`, "x-hotel": hotel };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
      const [roomsRes, statusRes, availRes] = await Promise.all([
        axios.get(`${API_BASE}/api/rooms`, { headers: authHeaders }),
        axios.get(`${API_BASE}/api/dashboard/room-status`, { headers: authHeaders }),
        axios.get(`${API_BASE}/api/rooms/availability/date/${todayStr}`, { headers: authHeaders }),
      ]);
      setRooms((roomsRes.data || []).map(normalizeRoom));
      setRoomStatusCounts(statusRes.data);
      // Build set of room IDs that are booked today (availability = "false")
      const occupied = new Set<string>(
        (availRes.data || [])
          .filter((r: any) => r.availability === "false" || r.availability === false)
          .map((r: any) => String(r.roomid))
      );
      setTodayOccupiedIds(occupied);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  useEffect(() => {
    if (tab !== "inhouse") return;
    setInHouseLoading(true);
    axios.get(`${API_BASE}/api/dashboard/rooms-inhouse`, { headers: authHeaders })
      .then((r) => setInHouseRooms(r.data || []))
      .catch(() => {})
      .finally(() => setInHouseLoading(false));
  }, [tab]);

  const loadOnHold = useCallback(() => {
    setOnHoldLoading(true);
    axios.get(`${API_BASE}/api/dashboard/on-hold`, { headers: authHeaders })
      .then((r) => setOnHoldList(r.data || []))
      .catch(() => setOnHoldList([]))
      .finally(() => setOnHoldLoading(false));
  }, []);

  useEffect(() => {
    if (tab !== "onhold") return;
    loadOnHold();
  }, [tab, loadOnHold]);

  const handleHoldAction = async (bookingid: string, action: "confirm" | "release") => {
    setOnHoldActing(bookingid);
    try {
      await axios.post(`${API_BASE}/api/dashboard/on-hold/${bookingid}/${action}`, {}, { headers: authHeaders });
      setOnHoldList((prev) => prev.filter((h) => h.bookingid !== bookingid));
      showToast(action === "confirm" ? "Hold confirmed — room booked." : "Hold released — room freed.");
      fetchRooms(); // refresh availability counts
    } catch (err: any) {
      showToast(err?.response?.data?.error ?? "Action failed.");
    } finally {
      setOnHoldActing("");
    }
  };

  // Fetch guests + seasons once for the booking form
  useEffect(() => {
    axios.get(`${API_BASE}/api/dashboard/guests`, { headers: authHeaders })
      .then((r) => {
        if (Array.isArray(r.data)) {
          setGuestsList(r.data.map((g: any) => ({
            id: g.id, name: g.name, phoneNumber: String(g.phoneNumber ?? ""), email: g.email,
          })));
        }
      }).catch(() => {});
    axios.get(`${API_BASE}/api/seasons`, { headers: authHeaders })
      .then((r) => setSeasonsList(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const openBookingForRoom = (r: any) => {
    const today = new Date().toISOString().split("T")[0];
    setBookingFormInitial({
      ...emptyBookingForm,
      roomId: r.roomId,
      roomIds: [r.roomId],
      checkIn: today,
    });
    setBookingFormOpen(true);
  };

  const handleSaved = (room: Room) => {
    setRooms(prev => {
      const idx = prev.findIndex(r => r.roomId === room.roomId);
      if (idx >= 0) { const next = [...prev]; next[idx] = room; return next; }
      return [...prev, room];
    });
    setFormOpen(false);
    showToast(formMode === "add" ? "Room added." : "Room updated.");
  };

  const handleDelete = async (room: Room) => {
    try {
      await axios.delete(`${API_BASE}/api/rooms/${room.roomId}`, { headers: authHeaders });
      setRooms(prev => prev.filter(r => r.roomId !== room.roomId));
      setDeleteConfirm(null);
      showToast("Room deleted.");
    } catch (err: any) {
      showToast(err?.response?.data?.error ?? "Delete failed.");
    }
  };

  // Bookable toggle: "out of service" blocks the room from BOTH reception and the bot.
  const isBlocked = (s?: string) => String(s ?? "").toLowerCase().replace(/\s+/g, "-") === "out-of-service";
  const toggleBookable = async (room: Room) => {
    const blocked = isBlocked(room.status);
    const newStatus = blocked ? "available" : "out of service";
    try {
      await axios.put(`${API_BASE}/api/rooms/${room.roomId}`, { status: newStatus }, { headers: authHeaders });
      setRooms(prev => prev.map(x => x.roomId === room.roomId ? { ...x, status: newStatus } : x));
      showToast(blocked ? `Room ${room.roomNumber} is now bookable` : `Room ${room.roomNumber} blocked from booking`);
    } catch (err: any) {
      showToast(err?.response?.data?.error ?? "Update failed.");
    }
  };

  // Bulk toggle a whole room type bookable/blocked
  const toggleTypeBookable = async (roomType: string, makeBookable: boolean) => {
    const targets = rooms.filter(r => (r.roomType || "") === roomType);
    try {
      await Promise.all(targets.map(r =>
        axios.put(`${API_BASE}/api/rooms/${r.roomId}`, { status: makeBookable ? "available" : "out of service" }, { headers: authHeaders })
      ));
      const ns = makeBookable ? "available" : "out of service";
      setRooms(prev => prev.map(x => (x.roomType || "") === roomType ? { ...x, status: ns } : x));
      showToast(`${roomType}: ${targets.length} room${targets.length !== 1 ? "s" : ""} ${makeBookable ? "bookable" : "blocked"}`);
    } catch (err: any) {
      showToast(err?.response?.data?.error ?? "Bulk update failed.");
    }
  };

  const filtered = useMemo(() => rooms.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || String(r.roomNumber).includes(q)
      || (r.roomType ?? "").toLowerCase().includes(q)
      || (r.bedType ?? "").toLowerCase().includes(q)
      || (r.location ?? "").toLowerCase().includes(q)
      || (r.view ?? "").toLowerCase().includes(q);
    const effectiveSt = todayOccupiedIds.has(r.roomId) ? "occupied" : normalizeStatus(r.status);
    const matchStatus = statusFilter === "all" || effectiveSt === normalizeStatus(statusFilter);
    return matchSearch && matchStatus;
  }), [rooms, search, statusFilter, todayOccupiedIds]);

  // Use accurate counts from API (considers active bookings), fall back to local rooms.status
  // Occupied = rooms with today's date booked in rooms_availability
  const counts = {
    occupied:     todayOccupiedIds.size,
    available:    rooms.filter(r => !todayOccupiedIds.has(r.roomId) && normalizeStatus(r.status) === "available").length,
    maintenance:  rooms.filter(r => !todayOccupiedIds.has(r.roomId) && normalizeStatus(r.status) === "maintenance").length,
    outOfService: rooms.filter(r => !todayOccupiedIds.has(r.roomId) && (normalizeStatus(r.status) === "out-of-service" || normalizeStatus(r.status) === "out of service")).length,
  };

  const roomStats = useMemo(() => {
    const totalCapacity = rooms.reduce((s, r) => s + (Number(r.capacity) || 0), 0);
    const withBalcony   = rooms.filter(r => r.balconyTerrace).length;
    const sizes         = rooms.map(r => r.sizeSqm != null ? Number(r.sizeSqm) : null).filter((s): s is number => s !== null && !isNaN(s) && s > 0);
    const avgSize       = sizes.length ? Math.round(sizes.reduce((a, b) => a + b, 0) / sizes.length) : 0;
    const premiumViews  = rooms.filter(r => ["Sea","Pool","Mountain"].includes(r.view)).length;
    const typeMap: Record<string, number> = {};
    rooms.forEach(r => { const t = r.roomType || "Unknown"; typeMap[t] = (typeMap[t] || 0) + 1; });
    const topType = Object.entries(typeMap).sort((a, b) => b[1] - a[1])[0];
    return { totalCapacity, withBalcony, avgSize, premiumViews, topType };
  }, [rooms]);

  return (
    <DashboardLayout>
      <div className="rp-page">

        {/* Header */}
        <div className="rp-header">
          <div>
            <h1 className="rp-title"><FontAwesomeIcon icon={faBed} /> Rooms</h1>
            <p className="rp-subtitle">{rooms.length} total rooms</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {/* Tab switcher */}
            <div className="rp-tab-switch">
              <button className={`rp-tab-sw ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>
                <FontAwesomeIcon icon={faList} /> List
              </button>
              <button className={`rp-tab-sw ${tab === "calendar" ? "active" : ""}`} onClick={() => setTab("calendar")}>
                <FontAwesomeIcon icon={faCalendarDays} /> Calendar
              </button>
              <button className={`rp-tab-sw ${tab === "inhouse" ? "active" : ""}`} onClick={() => setTab("inhouse")}>
                <FontAwesomeIcon icon={faUsers} /> In House
              </button>
              <button className={`rp-tab-sw ${tab === "onhold" ? "active" : ""}`} onClick={() => setTab("onhold")}>
                <FontAwesomeIcon icon={faHourglassHalf} /> On Hold
                {onHoldList.length > 0 && <span className="rp-tab-badge">{onHoldList.length}</span>}
              </button>
              <button className={`rp-tab-sw ${tab === "rates" ? "active" : ""}`} onClick={() => setTab("rates")}>
                <FontAwesomeIcon icon={faTag} /> Rates
              </button>
              <button className={`rp-tab-sw ${tab === "type-rates" ? "active" : ""}`} onClick={() => setTab("type-rates")}>
                <FontAwesomeIcon icon={faTag} /> Avg Prices
              </button>
              <button className={`rp-tab-sw ${tab === "photos" ? "active" : ""}`} onClick={() => setTab("photos")}>
                <FontAwesomeIcon icon={faImage} /> Photos
              </button>
            </div>
            <button className="rp-btn primary" onClick={() => { setFormMode("add"); setEditRoom(undefined); setFormOpen(true); }}>
              <FontAwesomeIcon icon={faPlus} /> Add Room
            </button>
          </div>
        </div>

        {/* Analytics */}
        <div className="rp-analytics">
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(201,168,76,0.12)", color: "#C9A84C" }}>
              <FontAwesomeIcon icon={faBed} />
            </div>
            <span className="rp-stat-value">{rooms.length}</span>
            <span className="rp-stat-label">Total Rooms</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(52,211,153,0.12)", color: "#34d399" }}>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <span className="rp-stat-value">{counts.available}</span>
            <span className="rp-stat-label">Available</span>
            <span className="rp-stat-pct">{rooms.length ? Math.round((counts.available / rooms.length) * 100) : 0}%</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(245,158,11,0.12)", color: "#fbbf24" }}>
              <FontAwesomeIcon icon={faBed} />
            </div>
            <span className="rp-stat-value">{counts.occupied}</span>
            <span className="rp-stat-label">Occupied</span>
            <span className="rp-stat-pct">{rooms.length ? Math.round((counts.occupied / rooms.length) * 100) : 0}%</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(96,165,250,0.12)", color: "#c9a84c" }}>
              <FontAwesomeIcon icon={faWrench} />
            </div>
            <span className="rp-stat-value">{counts.maintenance}</span>
            <span className="rp-stat-label">Maintenance</span>
            <span className="rp-stat-pct">{rooms.length ? Math.round((counts.maintenance / rooms.length) * 100) : 0}%</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(239,68,68,0.12)", color: "#f87171" }}>
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </div>
            <span className="rp-stat-value">{counts.outOfService}</span>
            <span className="rp-stat-label">Out of Service</span>
            <span className="rp-stat-pct">{rooms.length ? Math.round((counts.outOfService / rooms.length) * 100) : 0}%</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(6,182,212,0.12)", color: "#22d3ee" }}>
              <FontAwesomeIcon icon={faChartPie} />
            </div>
            <span className="rp-stat-value">
              {rooms.length ? Math.round((counts.occupied / rooms.length) * 100) : 0}%
            </span>
            <span className="rp-stat-label">Occupancy Rate</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(16,185,129,0.12)", color: "#34d399" }}>
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <span className="rp-stat-value">{roomStats.totalCapacity}</span>
            <span className="rp-stat-label">Total Capacity</span>
            <span className="rp-stat-pct">guests max</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}>
              <FontAwesomeIcon icon={faExpand} />
            </div>
            <span className="rp-stat-value">{roomStats.avgSize > 0 ? `${roomStats.avgSize}m²` : "—"}</span>
            <span className="rp-stat-label">Avg Room Size</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(201,168,76,0.12)", color: "#c9a84c" }}>
              <FontAwesomeIcon icon={faWind} />
            </div>
            <span className="rp-stat-value">{roomStats.withBalcony}</span>
            <span className="rp-stat-label">With Balcony</span>
            <span className="rp-stat-pct">{rooms.length ? Math.round((roomStats.withBalcony / rooms.length) * 100) : 0}%</span>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-icon" style={{ background: "rgba(14,165,233,0.12)", color: "#38bdf8" }}>
              <FontAwesomeIcon icon={faEye} />
            </div>
            <span className="rp-stat-value">{roomStats.premiumViews}</span>
            <span className="rp-stat-label">Premium Views</span>
            <span className="rp-stat-pct">Sea / Pool / Mountain</span>
          </div>
          {roomStats.topType && (
            <div className="rp-stat">
              <div className="rp-stat-icon" style={{ background: "rgba(236,72,153,0.12)", color: "#f472b6" }}>
                <FontAwesomeIcon icon={faBed} />
              </div>
              <span className="rp-stat-value" style={{ fontSize: 16 }}>{roomStats.topType[0]}</span>
              <span className="rp-stat-label">Most Common Type</span>
              <span className="rp-stat-pct">{roomStats.topType[1]} rooms</span>
            </div>
          )}
        </div>

        {tab === "list" && (
          <>
            {/* Filters */}
            <div className="rp-filters">
              <input type="text" placeholder="Search by number, type, bed, view…" value={search}
                onChange={e => setSearch(e.target.value)} className="rp-search" />
              <div className="rp-status-tabs">
                {["all", ...STATUS_OPTIONS].map(s => (
                  <button key={s} className={`rp-tab ${statusFilter === s ? "active" : ""}`} onClick={() => setStatusFilter(s)}>
                    {s === "all" ? "All" : displayStatus(s)}
                  </button>
                ))}
              </div>
            </div>

            {/* Bookability by room type — block/allow a whole type at once */}
            <div className="rp-type-book-bar">
              <span className="rp-type-book-title">Bookable types</span>
              {[...new Set(rooms.map(r => r.roomType).filter(Boolean))].map(type => {
                const typeRooms = rooms.filter(r => r.roomType === type);
                const blockedCount = typeRooms.filter(r => isBlocked(r.status)).length;
                const allBlocked = blockedCount === typeRooms.length && typeRooms.length > 0;
                return (
                  <button
                    key={type}
                    className={`rp-type-book-chip ${allBlocked ? "blocked" : "bookable"}`}
                    onClick={() => toggleTypeBookable(type as string, allBlocked)}
                    title={allBlocked ? `Click to make all ${type} bookable` : `Click to block all ${type} from booking`}
                  >
                    {type} <span className="rp-type-book-count">{typeRooms.length - blockedCount}/{typeRooms.length}</span>
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="rp-loading">Loading rooms…</div>
            ) : (
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead>
                    <tr>
                      <th>Room</th><th>Type</th><th>Status</th><th>Bed</th>
                      <th>Capacity</th><th>Floor</th><th>Size</th><th>View</th><th>Balcony</th><th>Extra Bed</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={11} className="rp-empty">No rooms found</td></tr>
                    ) : filtered
                        .slice()
                        .sort((a, b) => Number(a.roomNumber) - Number(b.roomNumber))
                        .map(r => {
                          const isOccupied = todayOccupiedIds.has(r.roomId);
                          const displaySt = isOccupied ? "occupied" : r.status;
                          const sc = statusColor(displaySt);
                          return (
                            <tr key={r.roomId}>
                              <td><span className="rp-room-number">#{r.roomNumber}</span></td>
                              <td>{r.roomType || "—"}</td>
                              <td>
                                <span className="rp-status-badge" style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}>
                                  {displayStatus(displaySt)}
                                </span>
                              </td>
                              <td>{r.bedType || "—"}</td>
                              <td>{r.capacity}</td>
                              <td>{r.floor ?? "—"}</td>
                              <td>{r.sizeSqm != null ? `${r.sizeSqm} m²` : "—"}</td>
                              <td>{r.view || "—"}</td>
                              <td style={{ textAlign: "center" }}>
                                {r.balconyTerrace
                                  ? <FontAwesomeIcon icon={faCheck} style={{ color: "#34d399" }} />
                                  : <span style={{ color: "rgba(242,235,217,0.35)" }}>—</span>}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {r.extraBed
                                  ? <FontAwesomeIcon icon={faCheck} style={{ color: "#34d399" }} />
                                  : <span style={{ color: "rgba(242,235,217,0.35)" }}>—</span>}
                              </td>
                              <td>
                                <div className="rp-actions">
                                  <button
                                    className={`rp-bookable-toggle ${isBlocked(r.status) ? "blocked" : "bookable"}`}
                                    title={isBlocked(r.status) ? "Blocked — click to make bookable" : "Bookable — click to block"}
                                    onClick={() => toggleBookable(r)}
                                  >
                                    {isBlocked(r.status) ? "Blocked" : "Bookable"}
                                  </button>
                                  <button className="rp-icon-btn edit"
                                    onClick={() => { setEditRoom(r); setFormMode("edit"); setFormOpen(true); }}>
                                    <FontAwesomeIcon icon={faPen} />
                                  </button>
                                  <button className="rp-icon-btn delete" onClick={() => setDeleteConfirm(r)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "calendar" && (
          <RoomCalendar rooms={rooms} authHeaders={authHeaders} seasons={seasonsList} />
        )}

        {tab === "rates" && (
          <RoomRatesCalendar
            rooms={rooms.map(r => ({ roomId: r.roomId, roomNumber: r.roomNumber, roomType: r.roomType }))}
            authHeaders={authHeaders}
          />
        )}

        {tab === "type-rates" && (
          <RoomTypeRatesTab rooms={rooms} authHeaders={authHeaders} onRoomsChanged={fetchRooms} />
        )}

        {tab === "photos" && (
          <RoomTypePhotosTab rooms={rooms} authHeaders={authHeaders} />
        )}

        {/* IN HOUSE TAB */}
        {tab === "inhouse" && (
          <div className="rp-inhouse">
            {inHouseLoading ? (
              <div className="rp-inhouse-loading">Loading rooms…</div>
            ) : (
              <div className="rp-inhouse-grid">
                {inHouseRooms.map((r) => (
                  <div
                    key={r.roomId}
                    className={`rp-inhouse-card ${r.occupied ? "occupied" : "empty"}`}
                    onClick={() => { if (!r.occupied) openBookingForRoom(r); }}
                    style={!r.occupied ? { cursor: "pointer" } : undefined}
                  >
                    <div className="rp-inhouse-card-header">
                      <span className="rp-inhouse-room-num">Room {r.roomNumber}</span>
                      <span className={`rp-inhouse-status-dot ${r.occupied ? "occ" : "avail"}`} />
                    </div>
                    <p className="rp-inhouse-type">{r.roomType || "—"}</p>
                    {r.occupied && r.booking ? (
                      <div className="rp-inhouse-guest">
                        <div className="rp-inhouse-avatar">{r.booking.guestName?.[0] ?? "?"}</div>
                        <div className="rp-inhouse-guest-info">
                          <p className="rp-inhouse-guest-name">{r.booking.guestName}</p>
                          {r.booking.phone !== "—" && <p className="rp-inhouse-guest-phone">{r.booking.phone}</p>}
                        </div>
                      </div>
                    ) : (
                      <div className="rp-inhouse-vacant">Vacant — click to add booking</div>
                    )}
                    {r.occupied && r.booking && (
                      <div className="rp-inhouse-meta">
                        {r.booking.dayUse && (
                          <div style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", borderRadius: 20, padding: "2px 8px", fontWeight: 700 }}>Day Use</span>
                          </div>
                        )}
                        {r.booking.bookingType && r.booking.bookingType !== "Standard" && (
                          <div style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: 10, background: "rgba(16,185,129,0.12)", color: "#065f46", borderRadius: 20, padding: "2px 8px", fontWeight: 700 }}>{r.booking.bookingType}</span>
                          </div>
                        )}
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Guests</span>
                          <span className="rp-inhouse-meta-val">
                            {r.booking.guestCount ?? 1} adult{(r.booking.guestCount ?? 1) !== 1 ? "s" : ""}
                            {(r.booking.childrenCount ?? 0) > 0 ? `, ${r.booking.childrenCount} child${r.booking.childrenCount !== 1 ? "ren" : ""}` : ""}
                          </span>
                        </div>
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Check-in</span>
                          <span className="rp-inhouse-meta-val">{r.booking.checkIn ? new Date(r.booking.checkIn).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}</span>
                        </div>
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Check-out</span>
                          <span className="rp-inhouse-meta-val">{r.booking.checkOut ? new Date(r.booking.checkOut).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}</span>
                        </div>
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Nights</span>
                          <span className="rp-inhouse-meta-val">{r.booking.dayUse ? "Day Use" : r.booking.nights}</span>
                        </div>
                        <div className="rp-inhouse-divider" />
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Room Price</span>
                          <span className="rp-inhouse-meta-val">EGP {(r.booking.roomPrice ?? 0).toLocaleString()}</span>
                        </div>
                        <div
                          className="rp-inhouse-meta-row rp-inhouse-orders-row"
                          onClick={() => openRoomOrders(r.roomNumber)}
                          style={{ cursor: "pointer" }}
                          title="View this room's orders"
                        >
                          <span className="rp-inhouse-meta-label">Orders ›</span>
                          <span className="rp-inhouse-meta-val" style={{ color: "#C9A84C" }}>EGP {(r.booking.orderPrice ?? 0).toLocaleString()}</span>
                        </div>
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Total</span>
                          <span className="rp-inhouse-meta-val" style={{ color: "#F2EBD9", fontWeight: 700 }}>EGP {r.booking.total.toLocaleString()}</span>
                        </div>
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Paid</span>
                          <span className="rp-inhouse-meta-val rp-inhouse-paid">EGP {r.booking.paid.toLocaleString()}</span>
                        </div>
                        <div className="rp-inhouse-meta-row">
                          <span className="rp-inhouse-meta-label">Remaining</span>
                          <span className="rp-inhouse-meta-val" style={{ color: r.booking.remaining > 0 ? "#ef4444" : "#34d399" }}>
                            EGP {r.booking.remaining.toLocaleString()}
                          </span>
                        </div>
                        <div className="rp-inhouse-pay-status">
                          <span className={`rp-inhouse-badge rp-badge-${r.booking.paymentStatus}`}>
                            {r.booking.paymentStatus}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ON HOLD TAB */}
        {tab === "onhold" && (
          <div className="rp-onhold">
            {onHoldLoading ? (
              <div className="rp-inhouse-loading">Loading holds…</div>
            ) : onHoldList.length === 0 ? (
              <div className="rp-onhold-empty">
                <FontAwesomeIcon icon={faHourglassHalf} style={{ fontSize: 28, opacity: 0.4, marginBottom: 10 }} />
                <div>No rooms are currently on hold.</div>
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>WhatsApp bookings awaiting their deposit will appear here.</div>
              </div>
            ) : (
              <div className="rp-onhold-grid">
                {onHoldList.map((h) => {
                  const msLeft = new Date(h.expiresAt).getTime() - Date.now();
                  const expired = h.expired || msLeft <= 0;
                  const hrs = Math.floor(msLeft / 3600000);
                  const mins = Math.floor((msLeft % 3600000) / 60000);
                  const timeLeft = expired ? "Expired" : hrs >= 1 ? `${hrs}h ${mins}m left` : `${mins}m left`;
                  const urgent = !expired && msLeft < 6 * 3600000;
                  const acting = onHoldActing === h.bookingid;
                  return (
                    <div key={h.bookingid} className={`rp-onhold-card ${expired ? "expired" : ""}`}>
                      <div className="rp-onhold-card-top">
                        <div>
                          <div className="rp-onhold-guest">{h.guestname}</div>
                          {h.phone && <div className="rp-onhold-phone">{h.phone}</div>}
                        </div>
                        <span className={`rp-onhold-timer ${expired ? "exp" : urgent ? "urgent" : ""}`}>
                          <FontAwesomeIcon icon={faHourglassHalf} /> {timeLeft}
                        </span>
                      </div>

                      <div className="rp-onhold-rooms">
                        {(h.rooms || []).map((rid: string) => (
                          <span key={rid} className="rp-onhold-room-chip">{rid}</span>
                        ))}
                      </div>

                      <div className="rp-onhold-meta">
                        <div className="rp-onhold-meta-row">
                          <span>Dates</span>
                          <span>
                            {h.checkin ? new Date(h.checkin).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                            {" → "}
                            {h.checkout ? new Date(h.checkout).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                            {" · "}{h.nights}n
                          </span>
                        </div>
                        <div className="rp-onhold-meta-row">
                          <span>Total</span>
                          <span style={{ color: "#F2EBD9", fontWeight: 700 }}>EGP {(h.total || 0).toLocaleString()}</span>
                        </div>
                        <div className="rp-onhold-meta-row">
                          <span>Deposit due</span>
                          <span style={{ color: "#C9A84C", fontWeight: 700 }}>EGP {(h.deposit || 0).toLocaleString()}</span>
                        </div>
                        <div className="rp-onhold-meta-row">
                          <span>Source</span>
                          <span style={{ textTransform: "capitalize" }}>{h.source}</span>
                        </div>
                        <div className="rp-onhold-meta-row">
                          <span>Booking</span>
                          <span style={{ fontFamily: "monospace", fontSize: 11 }}>{h.bookingid}</span>
                        </div>
                      </div>

                      <div className="rp-onhold-actions">
                        <button
                          className="rp-onhold-btn confirm"
                          disabled={acting}
                          onClick={() => handleHoldAction(h.bookingid, "confirm")}
                        >
                          <FontAwesomeIcon icon={faCheck} /> {acting ? "…" : "Confirm"}
                        </button>
                        <button
                          className="rp-onhold-btn release"
                          disabled={acting}
                          onClick={() => handleHoldAction(h.bookingid, "release")}
                        >
                          <FontAwesomeIcon icon={faXmark} /> {acting ? "…" : "Release"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Toast */}
        {toast && <div className="rp-toast">{toast}</div>}

        {/* Per-room orders drill-down */}
        {ordersModal && (
          <div className="rp-modal-overlay" onClick={() => setOrdersModal(null)}>
            <div className="rp-room-orders" onClick={e => e.stopPropagation()}>
              <div className="rp-room-orders-head">
                <h3>Room {ordersModal.roomNumber} — Orders</h3>
                <button className="rp-room-orders-close" onClick={() => setOrdersModal(null)}>✕</button>
              </div>
              {ordersModal.loading ? (
                <div className="rp-room-orders-empty">Loading…</div>
              ) : ordersModal.orders.length === 0 ? (
                <div className="rp-room-orders-empty">No orders for this room.</div>
              ) : (
                <>
                  <div className="rp-room-orders-list">
                    {ordersModal.orders.map((o: any) => (
                      <div key={o.id} className={`rp-room-order rp-ro-${o.status}`}>
                        <div className="rp-room-order-top">
                          <span className={`rp-room-order-badge rp-ro-badge-${o.status}`}>{o.status}</span>
                          <span className="rp-room-order-time">
                            {o.created_at ? new Date(o.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                          </span>
                          <span className="rp-room-order-total">EGP {Number(o.total || 0).toLocaleString()}</span>
                        </div>
                        <div className="rp-room-order-items">
                          {(o.items || []).map((it: any, i: number) => (
                            <span key={i} className="rp-room-order-chip">{it.qty}× {it.name}</span>
                          ))}
                          {(!o.items || o.items.length === 0) && <span className="rp-room-order-chip muted">no items</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rp-room-orders-foot">
                    <span>Total ordered</span>
                    <strong>EGP {ordersModal.orders.reduce((s: number, o: any) => s + Number(o.total || 0), 0).toLocaleString()}</strong>
                  </div>
                  <p className="rp-room-orders-note">
                    Delivered orders are billed to this room's folio (the “Orders” figure). Pending/cancelled aren't charged.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {deleteConfirm && (
          <div className="rp-modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="rp-confirm" onClick={e => e.stopPropagation()}>
              <p>Delete <strong>Room {deleteConfirm.roomNumber}</strong>? This cannot be undone.</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
                <button className="rp-btn secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="rp-btn danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {formOpen && (
          <RoomFormModal
            mode={formMode}
            room={editRoom}
            authHeaders={authHeaders}
            onClose={() => setFormOpen(false)}
            onSaved={handleSaved}
          />
        )}
      </div>

      {/* Booking form for vacant rooms */}
      {bookingFormOpen && (
        <BookingFormModal
          mode="add"
          initial={bookingFormInitial}
          guests={guestsList}
          rooms={rooms.map((r) => ({ roomId: r.roomId, roomNumber: String(r.roomNumber), roomType: r.roomType, status: r.status }))}
          seasons={seasonsList}
          allBookings={[]}
          authHeaders={authHeaders}
          onClose={() => setBookingFormOpen(false)}
          onSaved={() => {
            setBookingFormOpen(false);
            showToast("Booking added successfully");
            // Refresh in-house list
            axios.get(`${API_BASE}/api/dashboard/rooms-inhouse`, { headers: authHeaders })
              .then((r) => setInHouseRooms(r.data || [])).catch(() => {});
          }}
        />
      )}
    </DashboardLayout>
  );
}
